import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { Button, Flex, Form, Typography, Spin, Alert } from "antd";
import { useStores } from "stores";
import { _greaterOrEqual, _isRequired, _lessOrEqual, requestWithNotify } from "utils";
import { getCompanySettings, updateCompanySettings } from "services";
import { DefaultCheckbox, NumberInput } from "ui";
import { UpdateCompanySettingsRequest } from "interfaces";
import { useNotificationContext } from "providers";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";

export const DoubleActionsSettingsBlock = observer(() => {
  const { user } = useStores();
  const [form] = Form.useForm<UpdateCompanySettingsRequest>();
  const { openNotification } = useNotificationContext();

  const userId = user.userInfo?.id;
  const companyId = user.userInfo?.company_id;

  const { isSeniorCustomer, isSeniorLP } = user.roles;

  const [loading, setLoading] = useState(false);

  const doubleActionsValidation = Form.useWatch("doubleActionsValidation", form);

  useEffect(() => {
    if (userId && companyId) {
      setLoading(true);

      requestWithNotify({
        fetchFn: () => getCompanySettings(companyId),
        openNotification,
      })
        .then(({ response }) => {
          form.setFieldsValue(response);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [companyId, form, userId]);

  const canChangeBasicSettings = isSeniorCustomer || isSeniorLP;

  const disabled = loading || !canChangeBasicSettings;

  const onFinish = async (values: UpdateCompanySettingsRequest) => {
    if (companyId && userId) {
      setLoading(true);

      requestWithNotify({
        fetchFn: () =>
          updateCompanySettings(companyId, {
            ...values,
            doubleActionsValidationThreshold: values.doubleActionsValidationThreshold || 0,
          }),
        openNotification,
        successMsg: "Настройки успешно сохранены",
      })
        .then(() => {
          return requestWithNotify({
            fetchFn: () => getCompanySettings(companyId),
            openNotification,
            delay: 1500,
          });
        })
        .then(({ response }) => {
          form.setFieldsValue(response);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <Spin spinning={loading}>
      <Form form={form} onFinish={onFinish}>
        <Flex vertical gap="large">
          <Flex vertical gap="large">
            <Flex vertical gap="large">
              <Form.Item name="doubleActionsValidation" valuePropName="checked" noStyle>
                <DefaultCheckbox disabled={disabled} >
                  <Typography.Text className="whitebox-title no-selection">Двойной контроль действий</Typography.Text>
                </DefaultCheckbox>
              </Form.Item>

              {doubleActionsValidation && (
                <Flex vertical gap="small" style={{ maxWidth: 430 }}>
                  <Typography.Text className="text-bold">
                    Порог применения двойного контроля
                  </Typography.Text>
                  <Form.Item
                    name="doubleActionsValidationThreshold"
                    className="full-width"
                    validateDebounce={FORM_VALIDATION_DEBOUNCE}
                    rules={[_isRequired("Поле обязательно для ввода"), _greaterOrEqual(0)]}
                  >
                    <NumberInput disabled={disabled} placeholder="Привлекать согласующего при сумме заявки больше" />
                  </Form.Item>

                  <Typography.Text className="text-bold">
                    Действия, требующие дополнительного согласования
                  </Typography.Text>
                  <Form.Item name="destinationWalletVerification" valuePropName="checked" noStyle>
                    <DefaultCheckbox disabled={disabled}>Дополнительная верификация кошелька</DefaultCheckbox>
                  </Form.Item>
                </Flex>
              )}
            </Flex>
          </Flex>

          <div>
            <Button disabled={disabled} type="primary" htmlType="submit" loading={loading} size="middle">
              Сохранить
            </Button>
          </div>
        </Flex>
      </Form>
    </Spin>
  );
});
